import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'

import {
  SpotlightClinicLocatorSession,
  SpotlightClinicLocatorSessionApi,
  SpotlightEmbeddingDoctorsSession,
  SpotlightEmbeddingDoctorsSessionApi,
  SpotlightHeroBannerSession,
  SpotlightHeroBannerSessionApi,
  SpotlightRiskScreenerSession,
  SpotlightRiskScreenerSessionApi,
  SpotlightVideoSession,
  SpotlightVideoSessionApi,
  SpotlightClickableBannerSessionApi,
  ArticleApi,
  ListBanner,
} from 'interfaces/types'
import {
  ContentBannerSession,
  ContentBannerSessionApi,
  KeyPillar,
  KeyPillarsArticlesType,
  KeyPillarsArticlesTypeApi,
  KeyPillarsClickableBannerType,
  KeyPillarsClickableBannerTypeApi,
  ScheduleEventCard,
  ScheduleEventCardApi,
  ServiceItem,
  ServiceItemApi,
  SessionType,
  SpotlightArticleSession,
  SpotlightArticleSessionApi,
  SpotlightChatWithExpertSession,
  SpotlightChatWithExpertSessionApi,
  SpotlightClickableBannerSession,
  SpotlightClickableSingleBannerSession,
  SpotlightCommunityWithPostsSession,
  SpotlightCommunityWithPostsSessionApi,
  SpotlightEcommerceProductsSession,
  SpotlightEcommerceProductsSessionApi,
  SpotlightContentExpertSession,
  SpotlightContentExpertSessionApi,
  SpotlightFooterBannerSessionApi,
  SpotlightHealthToolListSession,
  SpotlightHealthToolListSessionApi,
  SpotlightHealthToolSession,
  SpotlightHealthToolSessionApi,
  SpotlightHeroBannerAppSession,
  SpotlightHeroBannerAppSessionApi,
  SpotlightLeadgenSession,
  SpotlightLeadgenSessionApi,
  SpotlightQuotationsSession,
  SpotlightQuotationsSessionApi,
  SpotlightScheduleEventSession,
  SpotlightScheduleEventSessionApi,
  SpotlightSessionCommonType,
  SpotlightSessionCommonTypeApi,
  SpotlightVideoSectionSessionApi,
  SpotlightVisionSession,
  SpotlightVisionSessionApi,
  TopServices,
  TopServicesApi,
  SpotlightMultiRiskScreenerSessionApi,
  SpotlightMultiRiskScreenerSession,
  SpotlightCustomizeBlockSessionApi,
  SpotlightCustomizeBlockSession,
  SpotlightCTABannerSessionApi,
  SpotlightCTABannerSession,
  CtaBannerApi,
  CtaBanner,
  KeyPillarBannerTypeApi,
  NonKeyPillarBannerTypeApi,
} from 'interfaces/types/CarePath'
import { PhoneVerifyType } from 'state/types'

import {
  formatArticle,
  formatCareServiceInfo,
  formatCategory,
  formatKeyPillarArticles,
  formatUserProfileUrl,
} from '.'

const formatCommonProps = (
  data: SpotlightSessionCommonTypeApi,
): SpotlightSessionCommonType => {
  return {
    wrapperProps: {
      title: data.title || '',
      description: data.description || '',
      icon: data.icon || '',
      bgColor: data.background_color || '',
    },
    sessionLayout: data.session_layout || '',
    idScroll: (data.id_scroll || '').toLowerCase(),
    title: data.title || '',
    description: data.description || '',
    icon: data.icon || '',
    hashtag: (() => {
      const hashtag = data.hashtag || ''
      return encodeURIComponent(
        hashtag.startsWith('#') ? hashtag.substring(1) : hashtag,
      ).toLowerCase()
    })(),
    backgroundColor: data.background_color || '',
  }
}

export const formatSpotlightRiskScreenerSession = (
  data: SpotlightRiskScreenerSessionApi,
): SpotlightRiskScreenerSession => {
  return {
    ...formatCommonProps(data),
    selectSubot:
      typeof data.select_subot === 'string'
        ? [data.select_subot]
        : data.select_subot || [],
  }
}

export const formatSpotlightMultiRiskScreenerSession = (
  data: SpotlightMultiRiskScreenerSessionApi,
): SpotlightMultiRiskScreenerSession => {
  return {
    ...formatCommonProps(data),
    horizontalDisplay: Boolean(data.horizontal_display),
    listSubot: (data.list_subot || []).map((i) => ({
      id: i.subot || '',
      backgroundColor: i.background_color || '',
      buttonColor: i.button_color || '',
    })),
  }
}

export const formatSpotlightHeroBannerSession = (
  data: SpotlightHeroBannerSessionApi,
): SpotlightHeroBannerSession => {
  return {
    ...formatCommonProps(data),
    imageBanner:
      (data.image_banner || []).length > 0
        ? data.image_banner.map((i) => ({
            mobileBanner: i.mobile_banner || '',
            desktopBanner: i.desktop_banner || '',
            linkTo: i.link_to || '',
            ctaTitle: i.cta_title || '',
            title: i.title || '',
            listBanner: [],
          }))
        : [
            {
              mobileBanner: data.mobile_banner || '',
              desktopBanner: data.desktop_banner || '',
              linkTo: data.link_to || '',
              ctaTitle: data.cta_title || '',
              title: data.title || '',
              listBanner: [],
            },
          ],
    listBanner: (() => {
      const listBanner = data.list_banner || []
      return listBanner.map((i) => ({
        desktopBanner: i.desktop_banner || '',
        mobileBanner: i.mobile_banner || '',
        ctaTitle: i.cta_title || '',
        linkTo: i.link_to || '',
        title: i.title || (listBanner.length === 1 ? data.title : ''),
      }))
    })(),
    isCtaBanner: Boolean(data.is_cta_banner),
    autoSlideTiming: Boolean(data.auto_slide)
      ? 7000
      : Number(data.auto_slide_timing || 0) * 1000,
    medicalFacts: {
      title: data.medical_facts?.title || '',
      listContent: data.medical_facts?.list_content || [],
    },
    navigating: (data.navigating || []).map((i) => ({
      idScroll: i.id_scroll || '',
      title: i.title || '',
      description: i.description || '',
      ctaTitle: i.cta_title || '',
      sessionLayout: i.session_layout || '',
      image: i.icon || '',
      isImageBg: Boolean(i.is_banner),
      url: i.url || '',
      type: i.type || 'normal',
    })),
    isDynamicBanner: Boolean(data.dynamic_banner),
  }
}

export const formatSpotlightHeroBannerAPPSession = (
  data: SpotlightHeroBannerAppSessionApi,
): SpotlightHeroBannerAppSession => {
  return {
    ...formatCommonProps(data),
    title: data.information?.title || '',
    banner: {
      desktop: data.banner?.desktop || '',
      mobile: data.banner?.mobile || '',
    },
    headerDescription: data.header_description || '',
    navigating: (data.navigating || []).map((i) => ({
      idScroll: i.id_scroll || '',
      title: i.title || '',
      description: i.description || '',
      ctaTitle: i.cta_title || '',
      sessionLayout: i.session_layout || '',
      image: i.icon || '',
      url: i.url || '',
      type: i.type || 'normal',
    })),
  }
}

export const formatSpotlightEmbeddingDoctorsSession = (
  data: SpotlightEmbeddingDoctorsSessionApi,
): SpotlightEmbeddingDoctorsSession => {
  return {
    ...formatCommonProps(data),
    typeEmbedding: data.type_embedding || 'doctor',
    embebIds: data.embeb_ids || [],
    specialties: data.specialties || [],
    doctors: [],
    campaign: {
      name: data.campaign?.name || '',
      source: data.campaign?.source || '',
    },
  }
}

export const formatSpotlightClinicLocatorSession = (
  data: SpotlightClinicLocatorSessionApi,
): SpotlightClinicLocatorSession => {
  return {
    ...formatCommonProps(data),
    specialties_hashtag: data?.specialties_hashtag || '',
    specialties: data.specialties || [],
  }
}

export const formatSpotlightVideoSession = (
  data: SpotlightVideoSessionApi,
): SpotlightVideoSession => {
  return {
    ...formatCommonProps(data),
    fullWidth: data.full_width || false,
    autoPlay: data.auto_play || false,
    isShortReel: Boolean(data.embed_short_reel),
    videoList:
      (data.video_list || []).length > 0
        ? data.video_list.map((i) => ({
            urlVideo: i.url_video,
            videoTitle: i.video_title,
          }))
        : data.feature_video
        ? [
            {
              urlVideo: data.feature_video.url_video,
              videoTitle: data.feature_video.video_title,
            },
          ]
        : [],
  }
}

export const formatSpotlightVideoSectionSession = (
  data: SpotlightVideoSectionSessionApi,
): SpotlightVideoSession => {
  return {
    ...formatCommonProps(data),
    fullWidth: true,
    autoPlay: false,
    title: data.title || '',
    videoList: [
      {
        urlVideo: data.video_url || '',
        videoTitle: '',
      },
    ],
  }
}

export const formatSpotlightArticleSession = (
  data: SpotlightArticleSessionApi,
): SpotlightArticleSession => {
  return {
    ...formatCommonProps(data),
    typeDisplay: data.type_display || 'grid',
    readMoreArticles: data.read_more_articles || [],
    ...(data.type_display === 'key-pillars'
      ? (() => {
          const listArticles = (data.list_articles ||
            []) as KeyPillarsArticlesTypeApi[]
          const keyPillars: KeyPillar[] = []
          const listing: KeyPillarsArticlesType[] = []
          listArticles.map((i: KeyPillarsArticlesTypeApi) => {
            const item = formatKeyPillarArticles(i)
            listing.push(item)
            keyPillars.push(item.keyPillars)
          })
          return {
            listingByKeyPillar: {
              listing,
              keyPillars,
            },
            listArticles: [],
          }
        })()
      : {
          listArticles: ((data.list_articles || []) as ArticleApi[]).map(
            formatArticle,
          ),
        }),
    listCategories: (data.list_categories || []).map(formatCategory),
  }
}

export const formatKeyPillarClickableBanner = (
  data: KeyPillarsClickableBannerTypeApi,
  token?: string,
): KeyPillarsClickableBannerType => {
  if (!data) return {} as KeyPillarsClickableBannerType

  return {
    banners: (data.banners || []).map((i) => ({
      ...i,
      ...(token && {
        url: String(token).startsWith('https') ? token : '#chatwoot',
      }),
    })),
    keyPillars: {
      name: data.key_pillars?.name || '',
      slug: data.key_pillars?.slug || '',
    },
  }
}

export const formatSpotlightClickableBannerSession = (
  data: SpotlightClickableBannerSessionApi,
): SpotlightClickableBannerSession => {
  return {
    ...formatCommonProps(data),
    isSingleBanner: data.is_single_banner || false,
    applyCarousel: Boolean(data.apply_carousel),
    requireLogin: Boolean(data.require_login),
    avatarIcon: data.avata_icon || '',
    token: data.token || '',
    leadgencode: data.leadgencode || {},
    expandAllCategories: Boolean(data.expand_all_categories),
    ...(Boolean(data.layout_key_pillars)
      ? {
          listing: ((data.list_banners ||
            []) as KeyPillarsClickableBannerTypeApi[]).map(
            (i: KeyPillarsClickableBannerTypeApi) =>
              formatKeyPillarClickableBanner(i, data.token),
          ),
        }
      : {
          listing: [
            {
              banners: (data.list_banners || []).map((i) => ({
                ...i,
                ...(data.token && {
                  url: String(data.token).startsWith('https')
                    ? data.token
                    : '#chatwoot',
                }),
              })) as ListBanner[],
            },
          ],
        }),
  }
}

export const formatSpotlightClickableSingleBannerSession = (
  data: SpotlightClickableBannerSessionApi,
): SpotlightClickableSingleBannerSession => {
  return {
    ...formatCommonProps(data),
    isSingleBanner: data.is_single_banner || false,
    requireLogin: Boolean(data.require_login),
    singleBanner: {
      icon: data.single_banner?.icon || '',
      banner: data.single_banner?.banner || '',
      url: data.single_banner?.url || '',
      ctaTitle: data.single_banner?.text_of_url || '',
    },
    disclaimer: {
      title: data.disclaimer?.title || '',
      description: data.disclaimer?.description || '',
    },
  }
}

export const formatEcommerceProducts = (
  data: SpotlightEcommerceProductsSessionApi,
): SpotlightEcommerceProductsSession => {
  return {
    ...formatCommonProps(data),
    productSkuList: data.product_sku || '',
    title: data.pcm_widget_title || '',
    products: [],
  }
}

export const formatCommunityWithPostsSession = (
  data: SpotlightCommunityWithPostsSessionApi,
): SpotlightCommunityWithPostsSession => {
  return {
    ...formatCommonProps(data),
    typeEmbed: data.type_embeb || '',
    communityId: data.community_id || '',
    postsIds: data.posts || [],
    posts: [],
    desktopBanner: data.desktop_banner || '',
    mobileBanner: data.mobile_banner || '',
    post: data.post || 0,
  }
}

export const formatLeadgenSession = (
  data: SpotlightLeadgenSessionApi,
): SpotlightLeadgenSession => {
  return {
    ...formatCommonProps(data),
    campaignCode: data.campaign_code || '',
    banner: {
      desktop: data.banner?.desktop || '',
      mobile: data.banner?.mobile || '',
    },
    termAndCondition: data.term_and_condition || '',
    incentiveMessage: {
      title: data.incentive_message?.title || '',
      description: data.incentive_message?.description || '',
    },
    thankYouMessage: {
      title: data.thank_you_message?.title || '',
      description: data.thank_you_message?.description || '',
    },
    phoneVerification: data.phone_verification || PhoneVerifyType.SMS,
    ctaButtonTitle: data.cta_button_title || '',
    isSkippable: data.is_skippable || false,
    isModal: data.popup_appearance || false,
    campaigns: data.campaigns || null,
  }
}

export const formatHealthToolSession = (
  data: SpotlightHealthToolSessionApi,
): SpotlightHealthToolSession => {
  return {
    ...formatCommonProps(data),
    healthTool: {
      id: data?.health_tool?.id || '',
      title: data?.health_tool?.title || '',
      icon: data?.health_tool?.icon || '',
      link: data?.health_tool?.link || '',
      subTitle: data?.health_tool?.title || '',
      template: data?.health_tool?.template || '',
      isSubot: data?.health_tool?.is_subot || false,
      disclaimer: data?.health_tool?.disclaimer || '',
      reviewedBy: {
        avatar: data?.health_tool?.reviewed_by?.avatar_thumbnail || '',
        reviewer:
          data?.health_tool?.reviewed_by?.display_name ||
          data?.health_tool?.reviewed_by?.name ||
          '',
        date: data?.reviewed_date || '',
        link: formatUserProfileUrl(data?.health_tool?.reviewed_by),
      },
    },
  }
}

export type SpotlightSessionApiType =
  | SpotlightHeroBannerSessionApi
  | SpotlightRiskScreenerSessionApi
  | SpotlightMultiRiskScreenerSessionApi
  | SpotlightEmbeddingDoctorsSessionApi
  | SpotlightClinicLocatorSessionApi
  | SpotlightVideoSessionApi
  | SpotlightArticleSessionApi
  | SpotlightClickableBannerSessionApi
  | SpotlightEcommerceProductsSessionApi
  | SpotlightCommunityWithPostsSessionApi
  | SpotlightChatWithExpertSessionApi
  | SpotlightLeadgenSessionApi
  | SpotlightHealthToolSessionApi
  | SpotlightHeroBannerAppSessionApi
  | SpotlightVisionSessionApi
  | SpotlightVideoSectionSessionApi
  | SpotlightHealthToolListSessionApi
  | ContentBannerSessionApi
  | SpotlightFooterBannerSessionApi
  | SpotlightQuotationsSessionApi
  | SpotlightScheduleEventSessionApi
  | SpotlightContentExpertSessionApi
  | SpotlightCustomizeBlockSessionApi
  | SpotlightCTABannerSessionApi
export type SpotlightSessionType =
  | SpotlightHeroBannerSession
  | SpotlightRiskScreenerSession
  | SpotlightMultiRiskScreenerSession
  | SpotlightEmbeddingDoctorsSession
  | SpotlightClinicLocatorSession
  | SpotlightVideoSession
  | SpotlightArticleSession
  | SpotlightClickableBannerSession
  | SpotlightClickableSingleBannerSession
  | SpotlightEcommerceProductsSession
  | SpotlightCommunityWithPostsSession
  | SpotlightChatWithExpertSession
  | SpotlightLeadgenSession
  | SpotlightHealthToolSession
  | SpotlightHealthToolListSession
  | SpotlightHeroBannerAppSession
  | SpotlightVisionSession
  | ContentBannerSession
  | SpotlightQuotationsSession
  | SpotlightScheduleEventSession
  | SpotlightContentExpertSession
  | SpotlightCustomizeBlockSession
  | SpotlightCTABannerSession
  | {
      sessionLayout: string
      idScroll: string
      hashtag: string
      backgroundColor: string
    }
export const formatSpotlightSession = (
  data: SpotlightSessionApiType,
): SpotlightSessionType => {
  return data.session_layout === SessionType.HERO_BANNER_DEFAULT ||
    data.session_layout === SessionType.HERO_BANNER_FULLWIDTH
    ? formatSpotlightHeroBannerSession(data as SpotlightHeroBannerSessionApi)
    : data.session_layout === SessionType.HERO_BANNER_APP
    ? formatSpotlightHeroBannerAPPSession(
        data as SpotlightHeroBannerAppSessionApi,
      )
    : data.session_layout === SessionType.FOOTER_BANNER
    ? formatSpotlightFooterBannerSession(
        data as SpotlightFooterBannerSessionApi,
      )
    : data.session_layout === SessionType.EMBEDDING_RISKSCREENER
    ? formatSpotlightRiskScreenerSession(
        data as SpotlightRiskScreenerSessionApi,
      )
    : data.session_layout === SessionType.EMBEDDING_RISKSCREENER_MULTI
    ? formatSpotlightMultiRiskScreenerSession(
        data as SpotlightMultiRiskScreenerSessionApi,
      )
    : data.session_layout === SessionType.EMBEDDING_DOCTORS
    ? formatSpotlightEmbeddingDoctorsSession(
        data as SpotlightEmbeddingDoctorsSessionApi,
      )
    : data.session_layout === SessionType.CLINIC_LOCATORS
    ? formatSpotlightClinicLocatorSession(
        data as SpotlightClinicLocatorSessionApi,
      )
    : data.session_layout === SessionType.VIDEOS
    ? formatSpotlightVideoSession(data as SpotlightVideoSessionApi)
    : data.session_layout === SessionType.VIDEO_SECTION
    ? formatSpotlightVideoSectionSession(
        data as SpotlightVideoSectionSessionApi,
      )
    : data.session_layout === SessionType.ARTICLES
    ? formatSpotlightArticleSession(data as SpotlightArticleSessionApi)
    : data.session_layout === SessionType.CLICKABLE_BANNER ||
      data.session_layout === SessionType.CHAT_WITH_EXPERT
    ? (data as SpotlightClickableBannerSessionApi).is_single_banner
      ? formatSpotlightClickableSingleBannerSession(
          data as SpotlightClickableBannerSessionApi,
        )
      : formatSpotlightClickableBannerSession(
          data as SpotlightClickableBannerSessionApi,
        )
    : data.session_layout === SessionType.ECOMMERCE_PRODUCS
    ? formatEcommerceProducts(data as SpotlightEcommerceProductsSessionApi)
    : data.session_layout === SessionType.COMMUNITY_WITH_POSTS
    ? formatCommunityWithPostsSession(
        data as SpotlightCommunityWithPostsSessionApi,
      )
    : data.session_layout === SessionType.LEADGEN
    ? formatLeadgenSession(data as SpotlightLeadgenSessionApi)
    : data.session_layout === SessionType.HEALTH_TOOL
    ? formatHealthToolSession(data as SpotlightHealthToolSessionApi)
    : data.session_layout === SessionType.HEALTH_TOOL_LIST
    ? formatSpotlightHealthToolListSession(
        data as SpotlightHealthToolListSessionApi,
      )
    : data.session_layout === SessionType.CONTENT_BANNER
    ? formatSpotlightContentBannerSession(data as ContentBannerSessionApi)
    : data.session_layout === SessionType.VISION_SECTION
    ? formatVisionSession(data as SpotlightVisionSessionApi)
    : data.session_layout === SessionType.QUOTATION
    ? formatQuotationsSession(data as SpotlightQuotationsSessionApi)
    : data.session_layout === SessionType.SCHEDULED_EVENT
    ? formatScheduleEventSession(data as SpotlightScheduleEventSessionApi)
    : data.session_layout === SessionType.CONTENT_EXPERT
    ? formatContentExpertSession(data as SpotlightContentExpertSessionApi)
    : data.session_layout === SessionType.CUSTOMIZED_BLOCK
    ? formatCustomizeBlockSession(data as SpotlightCustomizeBlockSessionApi)
    : data.session_layout === SessionType.CTA_BANNER
    ? formatCTABannerSession(data as SpotlightCTABannerSessionApi)
    : {
        sessionLayout: '',
        idScroll: '',
        hashtag: '',
        backgroundColor: '',
      }
}
export const formatServiceItem = (data: ServiceItemApi): ServiceItem => {
  return {
    categoryId: data.categories.id || '',
    services: (data?.services || []).map(formatCareServiceInfo),
  }
}
export const formatTopServices = (data: TopServicesApi): TopServices => {
  return {
    serviceList: (data?.service_list || []).map(formatServiceItem),
  }
}

export const formatSpotlightContentBannerSession = (
  data: ContentBannerSessionApi,
): ContentBannerSession => {
  return {
    ...formatCommonProps(data),
    singleLayout: Boolean(data.single_layout),
    listBanner: (data.list_banner || []).map((i) => ({
      icon: i.icon || '',
      title: i.title || '',
      description: i.description || '',
      url: i.url || '',
      banner: i.banner || '',
    })),
  }
}

export const formatSpotlightFooterBannerSession = (
  data: SpotlightFooterBannerSessionApi,
): SpotlightHeroBannerSession => {
  return {
    ...formatCommonProps(data),
    imageBanner: [
      {
        mobileBanner: data.banner_mobile || '',
        desktopBanner: data.banner_desktop || '',
        linkTo: data.url || '',
        listBanner: [],
        title: '',
        ctaTitle: '',
      },
    ],
    navigating: [],
    isCtaBanner: false,
    medicalFacts: {
      title: '',
      listContent: [],
    },
    listBanner: [],
    autoSlideTiming: 400,
    isDynamicBanner: true,
  }
}

export const formatSpotlightHealthToolListSession = (
  data: SpotlightHealthToolListSessionApi,
): SpotlightHealthToolListSession => {
  return {
    ...formatCommonProps(data),
    healthTools: (data.health_tools || []).map((i) => ({
      title: i.title || '',
      listHealthTools: (i.list_health_tools || []).map((j) => ({
        icon: j.icon || '',
        title: j.title || '',
        description: j.description || '',
        url: j.url || '',
        banner: '',
      })),
    })),
  }
}

export const formatVisionSession = (
  data: SpotlightVisionSessionApi,
): SpotlightVisionSession => {
  return {
    ...formatCommonProps(data),
    image: data.image || '',
  }
}

export const formatQuotationsSession = (
  data: SpotlightQuotationsSessionApi,
): SpotlightQuotationsSession => {
  return {
    ...formatCommonProps(data),
    icon: data.icon || '',
    quotations: (data.quotations || []).map((i) => ({
      name: i.name || '',
      message: i.message || '',
    })),
  }
}

export const formatScheduleEventCard = (
  data: ScheduleEventCardApi,
  index: number,
): ScheduleEventCard => {
  return {
    id: `card-${index}`,
    startDate: data.event_time?.start_date || '',
    endDate: data.event_time?.end_date || '',
    countdownStart: data.event_time?.countdown_start || '',
    ctaTitle: data.cta?.cta || '',
    ctaUrl: data.cta?.url || '',
    bannersDesktop: data.banner_event?.desktop || '',
    bannersMobile: data.banner_event?.mobile || '',
    leadgenCampaign: {
      code: data.leadgen_campaign?.campaign_code || '',
      bannerDesktop: data.leadgen_campaign?.desktop || '',
      bannerMobile: data.leadgen_campaign?.mobile || '',
      ctaTitle: data.leadgen_campaign?.cta_text || '',
      enableLeadgenInline: Boolean(data.leadgen_campaign?.visible_section),
    },
  }
}

export const formatScheduleEventSession = (
  data: SpotlightScheduleEventSessionApi,
): SpotlightScheduleEventSession => {
  return {
    ...formatCommonProps(data),
    maskcot: data.mascott || CommonGAssets.getAssetPath('tiki/maskcot.png'),
    listEvent: (data.list_event || []).map(formatScheduleEventCard),
  }
}

export const formatContentExpertSession = (
  data: SpotlightContentExpertSessionApi,
): SpotlightContentExpertSession => {
  return {
    ...formatCommonProps(data),
    doctors: (data.doctors || []).map((i) => ({
      name: i.display_name || '',
      avatar: i.avatar_thumbnail || '',
      url: i.profile_url || '',
      role: i.role || '',
      organisation: i.organisation || '',
      specialty: i.specialty || '',
      position: i.position || '',
    })),
  }
}

export const formatCustomizeBlockSession = (
  data: SpotlightCustomizeBlockSessionApi,
): SpotlightCustomizeBlockSession => {
  return {
    ...formatCommonProps(data),
    listing: (data.list_customize_block || []).map((i) => ({
      icon: i.icon_and_background?.icon || '',
      backgroundColor: i.icon_and_background?.background_color || '',
      header: {
        value: i.header?.value || '',
        color: i.header?.color || '',
      },
      content: {
        value: i.content?.value || '',
        color: i.content?.color || '',
      },
      description: {
        value: i.description?.value || '',
        color: i.description?.color || '',
      },
    })),
  }
}

export const formatCTABannerTypeSingleSession = (
  data: Extract<CtaBannerApi, { cta_banner_type: 'single-image' }>,
): Extract<CtaBanner, { type: 'single-image' }> => {
  if (!data) {
    return {
      type: 'single-image',
      layout: 'image_overlay_content',
      layoutPosition: 'left_banner',
      listing: [],
    }
  }

  const {
    cta_banner_type,
    layout,
    desktop_banner,
    mobile_banner,
    content_setting,
  } = data

  const header = content_setting?.header ?? {}
  const text = content_setting?.text ?? {}
  const buttons = content_setting?.buttons ?? {}
  const primaryButton = buttons?.primary ?? {}
  const secondaryButton = buttons?.secondary_button ?? {}

  const listing = [
    {
      banners: [
        {
          desktop: desktop_banner ?? '',
          mobile: mobile_banner ?? '',
          titleColor: header.text_color ?? '',
          title: header.header_content ?? '',
          descriptionColor: text.text_color ?? '',
          description: text.text_content ?? '',
          buttons: {
            primary: {
              backgroundColor: buttons.button_color ?? '',
              backgroundGradient: buttons.gradient_button ?? '',
              title: primaryButton.button_name ?? '',
              url: primaryButton.button_link ?? '',
              openNewTab: Boolean(buttons.open_new_tab),
            },
            secondary: {
              backgroundColor: buttons.button_color ?? '',
              backgroundGradient: buttons.gradient_button ?? '',
              title: secondaryButton.button_name ?? '',
              url: secondaryButton.button_link ?? '',
              openNewTab: Boolean(buttons.open_new_tab),
            },
          },
        },
      ],
    },
  ]

  return {
    type: cta_banner_type ?? 'single-image',
    layout: layout ?? 'image_overlay_content',
    layoutPosition: content_setting?.position ?? 'left_banner',
    listing,
  }
}

export const formatCTABannerTypeMultiSession = (
  data: Extract<CtaBannerApi, { cta_banner_type: 'multiple-image' }>,
): Extract<CtaBanner, { type: 'multiple-image' }> => {
  if (!data) {
    return {
      type: 'multiple-image',
      layout: 'image_overlay_button',
      fixSizeBanner: false,
      applyCarousel: false,
      layoutKeyPillars: false,
      expandAllCategories: false,
      listing: [],
    }
  }

  const {
    cta_banner_type,
    layout_key_pillars,
    layout,
    fix_size_banner,
    apply_carousel,
    expand_all_categories,
    list_banners,
    color_list_banner,
    open_new_tab,
  } = data

  const layoutKeyPillars = Boolean(layout_key_pillars)
  const defaultColor = {
    title: color_list_banner?.title_color ?? '',
    description: color_list_banner?.description_color ?? '',
    button: color_list_banner?.button_color ?? '',
    gradient: color_list_banner?.gradient_button ?? '',
  }

  const listingByLayout = layoutKeyPillars
    ? list_banners ?? []
    : [{ banners: list_banners }]

  const listing = listingByLayout.map((groupBanner) => {
    const isKeyPillar = layoutKeyPillars && 'key_pillars' in groupBanner
    const keyPillarData = isKeyPillar
      ? (groupBanner as KeyPillarBannerTypeApi).key_pillars ?? null
      : null

    const banners =
      (isKeyPillar
        ? (groupBanner as KeyPillarBannerTypeApi).banners
        : (list_banners as NonKeyPillarBannerTypeApi[])) ?? []

    return {
      keyPillars: {
        name: keyPillarData?.name || '',
        slug: keyPillarData?.slug || '',
      },
      banners: banners.map((banner) => {
        const setting = banner.banner_setting ?? {}
        return {
          desktop: banner.desktop ?? '',
          mobile: banner.mobile ?? '',
          titleColor: defaultColor.title,
          title: setting.title ?? '',
          descriptionColor: defaultColor.description,
          description: setting.description ?? '',
          buttons: {
            primary: {
              backgroundColor: defaultColor.button,
              backgroundGradient: defaultColor.gradient,
              title: setting.button_text ?? '',
              url: setting.button_link ?? '',
              openNewTab: Boolean(open_new_tab),
            },
          },
        }
      }),
    }
  })

  return {
    type: cta_banner_type ?? 'multiple-image',
    layout: layout ?? 'image_overlay_button',
    fixSizeBanner: Boolean(fix_size_banner),
    applyCarousel: Boolean(apply_carousel),
    layoutKeyPillars,
    expandAllCategories: Boolean(expand_all_categories),
    listing,
  }
}

export const formatCTABannerSession = (
  data: SpotlightCTABannerSessionApi,
): SpotlightCTABannerSession => {
  return {
    ...formatCommonProps(data),
    ...(data.cta_banner_type === 'single-image'
      ? formatCTABannerTypeSingleSession(data)
      : formatCTABannerTypeMultiSession(data)),
  }
}
